import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ThemePalette } from '@angular/material/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { ButtonComponent } from '@y42/core/button';
import { IconModule } from '@y42/core/icon';
import { KeyboardShortcutComponent } from '@y42/keyboard-shortcuts';
import { PlatformService } from '@y42/platform';
import { areArraysEqual } from '@y42/util/are-arrays-equal';
import uniq from 'lodash/uniq';
import { BehaviorSubject, combineLatest, filter, fromEvent, map, Observable, of, switchMap, tap } from 'rxjs';
import { DialogSize } from './dialog-v2.model';

@UntilDestroy()
@Component({
  selector: 'y42-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, IconModule, MatButtonModule, ButtonComponent, KeyboardShortcutComponent],
  templateUrl: './dialog-v2.component.html',
  styleUrls: ['./dialog-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogV2Component<R = unknown> {
  constructor() {
    this.confirmOnCmdEnter().pipe(untilDestroyed(this)).subscribe();
  }

  protected readonly dialogRef: MatDialogRef<DialogV2Component<unknown>, R | undefined | void> = inject(MatDialogRef);
  private readonly document = inject(DOCUMENT);

  private readonly matDialog = inject(MatDialog);
  private readonly initiallyOpenedDialogs = this.getCurrentlyOpenedDialogsIds();

  @Input()
  dialogTitle = '';

  @HostBinding('class.scrollable-body')
  @Input()
  scrollableBody = false;

  @Input()
  noBodyPadding = false;

  /**
   * The dialog's width. The default value is `medium`.
   */
  @Input()
  @InputObservable()
  width: DialogSize = 'medium';
  private readonly width$!: Observable<DialogSize | undefined>;

  @HostBinding(`attr.data-width`)
  get widthAttr() {
    return this.state.width;
  }

  /**
   * The dialog's height. The default value is `auto`.
   */
  @Input()
  @InputObservable()
  height: DialogSize = 'auto';
  private readonly height$!: Observable<DialogSize | undefined>;

  @HostBinding(`attr.data-height`)
  get heightAttr() {
    return this.state.height;
  }

  @Input() closeable = true;

  /**
   * A callback that is executed when the dialog is closed.
   *
   * When the callback returns `false` then the dialog will not be closed.
   */
  @Input() cancelFn: (() => Promise<void | boolean>) | null = null;

  /**
   * Disables confirming the dialog with CMD+Enter or CTRL+Enter.
   */
  @Input()
  @InputObservable()
  disableCmdEnter = false;
  private readonly disableCmdEnter$!: Observable<boolean>;

  /**
   * **Warning:** Works only with the default header template.
   */
  @Input() allowFullscreen = false;

  /**
   * When defined the dialog will have an additional confirmation button with given label.
   */
  @Input()
  @InputObservable()
  confirmLabel?: string | TemplateRef<unknown> | null;
  private readonly confirmLabel$!: Observable<string | TemplateRef<unknown> | null>;

  @Input() confirmColor?: ThemePalette | null = 'primary';
  @Input() confirmPending?: boolean | null = false;
  @Input() confirmDisabled?: boolean | null = false;

  /**
   * Defaults to `'Cancel'` when `confirmLabel` is defined.
   * Defaults to `'Close'` when `confirmLabel` is not defined.
   */
  @Input() cancelLabel?: string;

  @Output() confirm: EventEmitter<void> = new EventEmitter();

  readonly fullscreen$$ = new BehaviorSubject<boolean>(false);

  readonly state = connectState(this, {
    height: combineLatest([this.fullscreen$$, this.height$]).pipe(
      map(([fullscreen, height]) => (fullscreen ? 'fullscreen' : height)),
    ),
    width: combineLatest([this.fullscreen$$, this.width$]).pipe(
      map(([fullscreen, width]) => (fullscreen ? 'fullscreen' : width)),
    ),
    fullscreen: this.fullscreen$$,
    confirmLabelTemplate: this.confirmLabel$.pipe(
      map((label) => label instanceof TemplateRef),
      map((isTemplatRef) => (isTemplatRef ? (this.confirmLabel as TemplateRef<unknown>) : null)),
    ),
  });

  toggleFullscreen() {
    this.fullscreen$$.next(!this.fullscreen$$.value);
  }

  async cancelClicked() {
    if (this.cancelFn) {
      const result = await this.cancelFn();

      if (result === false) {
        return;
      }
    }

    this.dialogRef.close();
  }

  confirmed() {
    this.confirm.emit();
  }

  private confirmOnCmdEnter() {
    const platform = inject(PlatformService);

    return this.disableCmdEnter$.pipe(
      switchMap((disabled) =>
        // We need to handle the event during the capture phase
        // Otherwise a form input may capture the event first.
        // And mousetrap library (used by keyboard shortcuts) does not support capturing
        // https://github.com/ccampbell/mousetrap/pull/400.
        disabled
          ? of(null)
          : fromEvent<KeyboardEvent>(this.document, 'keydown', { capture: true }).pipe(
              filter((event) => event.key === 'Enter' && platform.eventHasCmdOrCtrlModifier(event)),
              filter(() => {
                return areArraysEqual(this.getCurrentlyOpenedDialogsIds(), this.initiallyOpenedDialogs);
              }),
              filter(() => !this.confirmDisabled && !this.confirmPending),
              tap(() => this.confirm.emit()),
            ),
      ),
    );
  }

  private getCurrentlyOpenedDialogsIds() {
    return uniq([...this.matDialog.openDialogs.map((matRef) => matRef.id), this.dialogRef.id]);
  }
}
